const AREAS_SERVED = [
  { "@type": "Country", "name": "South Africa" },
  { "@type": "City", "name": "Johannesburg", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gauteng" } },
  { "@type": "City", "name": "Pretoria", "containedInPlace": { "@type": "AdministrativeArea", "name": "Gauteng" } },
  { "@type": "City", "name": "Cape Town", "containedInPlace": { "@type": "AdministrativeArea", "name": "Western Cape" } },
  { "@type": "City", "name": "Durban", "containedInPlace": { "@type": "AdministrativeArea", "name": "KwaZulu-Natal" } }
];

const OPENING_HOURS_SPEC = [{
  "@type": "OpeningHoursSpecification",
  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  "opens": "08:00",
  "closes": "17:00"
}];

const CONTACT_POINT = {
  "@type": "ContactPoint",
  "contactType": "customer service",
  "telephone": "+27-73-250-9394",
  "email": "info@bookkeepingservices.co.za",
  "areaServed": "ZA",
  "availableLanguage": ["English", "Afrikaans"]
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "AccountingService",
  "name": "Bookkeeping Services South Africa",
  "description": "SAICA Registered bookkeeping and accounting services for South African businesses. We work with Xero, Sage, Pastel and other accounting systems depending on your business needs. Entry Level from R2,500/month.",
  "url": "https://bookkeepingservices.co.za",
  "email": "info@bookkeepingservices.co.za",
  "telephone": "+27-73-250-9394",
  "priceRange": "R300 - R4500/month",
  "areaServed": AREAS_SERVED,
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "ZA",
    "addressRegion": "Gauteng"
  },
  "openingHoursSpecification": OPENING_HOURS_SPEC,
  "contactPoint": CONTACT_POINT,
  "knowsAbout": [
    "Bookkeeping",
    "Payroll Services",
    "Tax Returns",
    "SARS Compliance",
    "Annual Financial Statements",
    "CIPC Annual Returns",
    "VAT Registration",
    "Company Registration"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Bookkeeping Service Packages",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "Basic Package",
        "price": "300",
        "priceCurrency": "ZAR",
        "description": "Nil turnover annual returns and CIPC compliance"
      },
      {
        "@type": "Offer",
        "name": "Entry Level Package",
        "price": "2500",
        "priceCurrency": "ZAR",
        "description": "SAICA Registered bookkeeping — monthly bookkeeping, payroll (1-5 employees), tax returns, EMP201, EMP501, UIF & CIPC. We work with Xero, Sage, Pastel and other accounting systems. Up to R2.3m turnover."
      },
      {
        "@type": "Offer",
        "name": "Medium Level Package",
        "price": "4500",
        "priceCurrency": "ZAR",
        "description": "Full-service bookkeeping — includes VAT201, payroll (1-10 employees), workmen's compensation & full compliance. We work with Xero, Sage, Pastel and other accounting systems. Up to R12m turnover."
      }
    ]
  },
  "sameAs": []
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "AccountingService", "ProfessionalService"],
  "@id": "https://bookkeepingservices.co.za/#business",
  "name": "Bookkeeping Services South Africa",
  "description": "SAICA registered bookkeeping and accounting services for South African businesses. Chartered Accountant-led practice offering payroll, tax returns, and SARS compliance from R300/month.",
  "url": "https://bookkeepingservices.co.za",
  "email": "info@bookkeepingservices.co.za",
  "telephone": "+27-73-250-9394",
  "priceRange": "R300 - R4500",
  "currenciesAccepted": "ZAR",
  "paymentAccepted": ["EFT", "Bank Transfer"],
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "ZA",
    "addressRegion": "Gauteng"
  },
  "areaServed": AREAS_SERVED,
  "serviceArea": { "@type": "Country", "name": "South Africa" },
  "openingHoursSpecification": OPENING_HOURS_SPEC,
  "openingHours": "Mo-Fr 08:00-17:00",
  "contactPoint": CONTACT_POINT,
  "knowsAbout": ["SAICA", "SARS Compliance", "Xero", "Sage", "Pastel", "QuickBooks", "Payroll", "Tax Returns"],
  "hasCredential": {
    "@type": "EducationalOccupationalCredential",
    "credentialCategory": "Professional Certification",
    "name": "SAICA Registered Chartered Accountant"
  }
};

// Helper for city / suburb landing pages.
// `path` is the full route path from root (e.g. "/areas/cape-town" or
// "/bookkeeping-services-cape-town"). Callers own the URL — this avoids
// hard-coding one URL shape and prevents wrong @id values on the /areas hubs.
export const buildCityLocalBusinessSchema = (city: string, province: string, path: string) => {
  const url = `https://bookkeepingservices.co.za${path.startsWith("/") ? path : `/${path}`}`;
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "AccountingService"],
    "@id": `${url}#business`,
    "name": `Bookkeeping Services ${city}`,
    "url": url,
    "email": "info@bookkeepingservices.co.za",
    "telephone": "+27-73-250-9394",
    "priceRange": "R300 - R4500",
    "currenciesAccepted": "ZAR",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "ZA",
      "addressLocality": city,
      "addressRegion": province
    },
    "areaServed": {
      "@type": "City",
      "name": city,
      "containedInPlace": { "@type": "AdministrativeArea", "name": province }
    },
    "openingHoursSpecification": OPENING_HOURS_SPEC,
    "openingHours": "Mo-Fr 08:00-17:00",
    "contactPoint": CONTACT_POINT,
    "knowsAbout": [
      "Bookkeeping",
      "Payroll Services",
      "Tax Returns",
      "SARS Compliance",
      "Annual Financial Statements",
      "CIPC Annual Returns"
    ]
  };
};

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What accounting software do you work with in South Africa?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We work with Xero, Sage, Pastel and other accounting systems depending on your business needs — including Sage Business Cloud Accounting, QuickBooks and Zoho Books. All support clean bank feeds from FNB, Standard Bank, ABSA, Nedbank, Capitec and Investec. If you already use a system we will continue on it, or migrate you across at no extra cost."
      }
    },
    {
      "@type": "Question",
      "name": "How quickly can you onboard my business?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most clients are fully onboarded within 48 hours. We provide a short onboarding checklist (CIPC documents, SARS tax number, bank feed authorisation and previous accounting records) and start invoicing only once everything is in place. If you need urgent SARS or CIPC submissions handled, we can usually act the same day."
      }
    },
    {
      "@type": "Question",
      "name": "Do you handle SARS audits, verifications and disputes?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Our registered tax practitioners handle SARS verifications, audits, requests for supporting documents, objections (NOO) and appeals (NOA) on your behalf via eFiling. We prepare the schedules SARS asks for, respond inside the deadlines and keep you copied on every step. There is no extra fee for routine verifications on submissions we filed."
      }
    },
    {
      "@type": "Question",
      "name": "What if my books are years behind?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We specialise in catch-up bookkeeping. We will quote separately for the backlog (priced per financial year), rebuild the books in your preferred accounting system (Xero, Sage, Pastel or other), file any outstanding VAT201, EMP201, EMP501, ITR14 and IRP6 returns, and bring your CIPC annual returns up to date so your company comes off the deregistration list."
      }
    },
    {
      "@type": "Question",
      "name": "Do you work with sole proprietors as well as Pty (Ltd) companies?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. We work with sole proprietors, partnerships, Pty (Ltd) companies, NPCs and trusts. Sole proprietors typically use our Basic package because they file an ITR12 individual return rather than an ITR14, and have no CIPC obligations."
      }
    },
    {
      "@type": "Question",
      "name": "Are you registered with SAICA and SARS?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Our practice is SAICA registered and our tax practitioners are registered with a Recognised Controlling Body (RCB) as required by SARS. This means we can legally submit returns on your behalf, sign off on annual financial statements, and represent you in dealings with SARS and CIPC."
      }
    },
    {
      "@type": "Question",
      "name": "Do you serve businesses outside the major cities?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Our service is fully remote, delivered via your chosen accounting software (Xero, Sage, Pastel or similar), so we work with businesses in every South African province — from Johannesburg, Cape Town and Durban through to smaller centres like Mbombela, Polokwane and George. All compliance submissions are handled through SARS eFiling and CIPC, neither of which requires physical presence."
      }
    },
    {
      "@type": "Question",
      "name": "What documents do you need to start?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "To onboard a Pty (Ltd) we need your CIPC registration documents (CoR 14.3 or CoR 15.1A), your SARS income tax number, bank statements for the period we are taking over, payroll details for any staff, and read-only access to any existing accounting software. We provide a one-page onboarding pack so nothing is missed."
      }
    }
  ]
};



export const siteNavigationSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SiteNavigationElement",
      "name": "Services",
      "description": "Bookkeeping, payroll, tax returns, SARS compliance and more",
      "url": "https://bookkeepingservices.co.za/services"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Packages & Pricing",
      "description": "SAICA Registered bookkeeping from R2,500/month — Xero, Sage, Pastel and other systems",
      "url": "https://bookkeepingservices.co.za/packages"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "About Us",
      "description": "SAICA registered bookkeeping professionals",
      "url": "https://bookkeepingservices.co.za/about"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Payroll Services",
      "description": "EMP201, EMP501, UIF declarations and payroll processing",
      "url": "https://bookkeepingservices.co.za/payroll-services"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Tax Returns & SARS",
      "description": "Annual tax returns, provisional tax, and SARS compliance",
      "url": "https://bookkeepingservices.co.za/tax-returns-sars-compliance"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Book a Call",
      "description": "Schedule a free consultation with our bookkeeping team",
      "url": "https://bookkeepingservices.co.za/book-a-call"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Contact",
      "description": "Get in touch with Bookkeeping Services South Africa",
      "url": "https://bookkeepingservices.co.za/contact"
    }
  ]
};

// Services page schema
export const servicesPageSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "provider": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa",
    "url": "https://bookkeepingservices.co.za"
  },
  "name": "Bookkeeping & Compliance Services",
  "description": "Professional bookkeeping, payroll, tax returns, and SARS compliance services for South African businesses.",
  "areaServed": { "@type": "Country", "name": "South Africa" },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Bookkeeping Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Monthly Bookkeeping", "description": "Accurate recording of all financial transactions including invoices, receipts, and bank statements." } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Payroll Processing (EMP201, EMP501)", "description": "Complete payroll processing including monthly EMP201 submissions and bi-annual EMP501 reconciliations." } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "UIF Monthly Declarations", "description": "Timely submission of UIF declarations to the Department of Labour." } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Annual Financial Statements", "description": "Professionally prepared annual financial statements that meet SARS requirements." } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Annual Tax Returns", "description": "Preparation and submission of annual income tax returns ensuring all deductions are claimed." } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "CIPC Annual Returns", "description": "On-time submission of your company's annual return to CIPC." } }
    ]
  }
};

// Packages page schema  
export const packagesPageSchema = {
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  "name": "Bookkeeping Service Packages — South Africa",
  "description": "Transparent, all-inclusive bookkeeping packages for South African businesses from R300/month.",
  "provider": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa"
  },
  "itemListElement": [
    {
      "@type": "Offer",
      "name": "Basic Package",
      "price": "300",
      "priceCurrency": "ZAR",
      "priceSpecification": { "@type": "UnitPriceSpecification", "price": "300", "priceCurrency": "ZAR", "unitText": "MONTH" },
      "description": "Nil turnover: annual tax return, 2x provisional tax, CIPC annual return, beneficial ownership."
    },
    {
      "@type": "Offer",
      "name": "Entry Level Package",
      "price": "2500",
      "priceCurrency": "ZAR",
      "priceSpecification": { "@type": "UnitPriceSpecification", "price": "2500", "priceCurrency": "ZAR", "unitText": "MONTH" },
      "description": "Bookkeeping up to 60 entries, payroll 1-5 employees, EMP201, EMP501, UIF, AFS, tax returns, CIPC. Turnover up to R2.3m."
    },
    {
      "@type": "Offer",
      "name": "Medium Level Package",
      "price": "4500",
      "priceCurrency": "ZAR",
      "priceSpecification": { "@type": "UnitPriceSpecification", "price": "4500", "priceCurrency": "ZAR", "unitText": "MONTH" },
      "description": "Bookkeeping up to 120 entries, VAT201, payroll 1-10 employees, workmen's compensation, full compliance. Turnover up to R12m."
    }
  ]
};

// Contact page schema
export const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Bookkeeping Services South Africa",
  "description": "Get in touch for professional bookkeeping, payroll, and SARS compliance services.",
  "url": "https://bookkeepingservices.co.za/contact",
  "mainEntity": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa",
    "email": "info@bookkeepingservices.co.za",
    "areaServed": { "@type": "Country", "name": "South Africa" },
    "openingHours": "Mo-Fr 08:00-17:00",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "info@bookkeepingservices.co.za",
      "availableLanguage": "English"
    }
  }
};

// About page schema
export const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Bookkeeping Services South Africa",
  "description": "SAICA registered Chartered Accountant-led bookkeeping practice serving SMEs, startups, and growing businesses across South Africa.",
  "url": "https://bookkeepingservices.co.za/about",
  "mainEntity": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa",
    "description": "SAICA registered bookkeeping practice",
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Professional Certification",
      "name": "SAICA Registered Chartered Accountant"
    },
    "knowsAbout": [
      "SAICA Standards",
      "SARS Compliance",
      "Xero Accounting",
      "Sage Accounting",
      "Pastel Accounting",
      "QuickBooks",
      "Payroll Processing",
      "Tax Returns",
      "CIPC Compliance"
    ]
  }
};

// Payroll service schema
export const payrollServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Payroll Services South Africa — EMP201, EMP501, UIF",
  "provider": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa",
    "url": "https://bookkeepingservices.co.za"
  },
  "description": "Complete payroll processing for South African businesses including EMP201 monthly submissions, EMP501 bi-annual reconciliations, and UIF declarations. SAICA registered.",
  "areaServed": { "@type": "Country", "name": "South Africa" },
  "serviceType": ["Payroll Processing", "EMP201 Submissions", "EMP501 Reconciliation", "UIF Declarations"],
  "offers": {
    "@type": "Offer",
    "description": "Payroll included in bookkeeping packages from R2,500/month",
    "priceCurrency": "ZAR"
  }
};

export const payrollFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is EMP201 and when is it due?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EMP201 is the monthly employer declaration submitted to SARS via eFiling. It reports PAYE, UIF and SDL deducted from employee salaries for the month. It is due by the 7th of the following month (or the last working day before the 7th if the 7th falls on a weekend or public holiday). Late submission attracts a 10% penalty on the amount due plus interest at the prescribed rate."
      }
    },
    {
      "@type": "Question",
      "name": "What is EMP501 and when is it submitted?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EMP501 is the bi-annual employer reconciliation that reconciles your EMP201 submissions, payments and IRP5/IT3(a) certificates for the period. The interim EMP501 covers March to August and is due by 31 October. The annual EMP501 covers the full tax year (March to February) and is due by 31 May. We prepare both, issue IRP5 certificates to your staff via EasyFile, and resolve any SARS reconciliation differences."
      }
    },
    {
      "@type": "Question",
      "name": "How do UIF contributions work in South Africa?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "UIF (Unemployment Insurance Fund) contributions are mandatory for almost all employers. The employer contributes 1% of remuneration and the employee contributes 1%, capped at a monthly remuneration ceiling set by the Department of Employment and Labour. The combined 2% is paid over to SARS on the EMP201. In addition, monthly UI-19 declarations are submitted to the UIF via uFiling so that employees can claim benefits."
      }
    },
    {
      "@type": "Question",
      "name": "Who has to pay the Skills Development Levy (SDL)?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "SDL is payable by employers whose total annual payroll exceeds R500,000. The rate is 1% of total remuneration and is declared on the EMP201 together with PAYE and UIF. SDL funds the SETAs and can be partially reclaimed through workplace skills plans and learnerships."
      }
    },
    {
      "@type": "Question",
      "name": "How is PAYE calculated for South African employees?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "PAYE is calculated on each employee's monthly remuneration using the current SARS tax tables and tax rebates (primary, secondary for over-65s, tertiary for over-75s). We apply the correct rebates, medical scheme tax credits, retirement annuity and pension fund deductions, travel allowances and any other adjustments before deducting PAYE. The deducted PAYE is paid over monthly via the EMP201."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need to register for PAYE if I only have one employee?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "If any employee earns above the income tax threshold (currently R95,750 per year for under-65s), you must register as an employer with SARS and deduct PAYE. UIF registration is required as soon as you employ anyone for more than 24 hours a month, regardless of salary level. We handle PAYE, UIF and SDL registration as part of onboarding."
      }
    },
    {
      "@type": "Question",
      "name": "What happens if I miss a payroll deadline?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "SARS applies an automatic 10% late payment penalty on the EMP201 amount, plus interest at the prescribed rate from the due date. Repeated late EMP501 reconciliations also trigger administrative penalties per month outstanding. Our clients do not miss these deadlines — we lodge and pay on or before the 7th every month."
      }
    },
    {
      "@type": "Question",
      "name": "How many employees can you process payroll for?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our Entry package covers payroll for 1–5 employees and our Medium package covers 1–10 employees. For teams of 11+ we provide a custom quote based on payslip volume, commission structures and any third-party benefits (medical, pension, RA, garnishees)."
      }
    },
    {
      "@type": "Question",
      "name": "Do you issue IRP5 certificates to my staff?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. We generate IRP5 and IT3(a) certificates through SARS EasyFile during each EMP501 cycle and distribute them to employees by email. Employees use these certificates to file their personal income tax returns."
      }
    },
    {
      "@type": "Question",
      "name": "Are you registered for COIDA / Workmen's Compensation?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We register clients with the Compensation Fund (COIDA) and file the annual Return of Earnings (W.As.8) every year. This is a legal requirement for any employer with one or more employees and is what gives your staff cover for workplace injuries."
      }
    }
  ]
};


// Tax & SARS compliance schema
export const taxSarsServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Tax Returns & SARS Compliance Services — South Africa",
  "provider": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa",
    "url": "https://bookkeepingservices.co.za"
  },
  "description": "Professional tax return preparation and SARS compliance services for South African businesses. Annual income tax, provisional tax, VAT201, and audit support by a SAICA registered practice.",
  "areaServed": { "@type": "Country", "name": "South Africa" },
  "serviceType": ["Annual Tax Returns", "Provisional Tax Returns", "VAT201 Submissions", "SARS Audit Support", "SARS Compliance"]
};

export const taxFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "When are company tax returns (ITR14) due in South Africa?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "An ITR14 must be submitted within 12 months of a company's financial year-end. For a company with a February year-end, the ITR14 is due by the following February. Late submission attracts SARS administrative penalties of between R250 and R16,000 per month outstanding, plus interest on any unpaid tax."
      }
    },
    {
      "@type": "Question",
      "name": "What is provisional tax (IRP6) and who must pay it?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Provisional tax is income tax paid in two compulsory instalments during the year of assessment, with an optional third top-up payment. It applies to all companies and to individuals who earn income other than a salary (rental, business income, dividends from foreign companies, freelance income above the threshold). The first IRP6 is due six months into the tax year and the second by the last day of the tax year. We calculate each IRP6 on actual year-to-date figures so you don't over- or under-pay."
      }
    },
    {
      "@type": "Question",
      "name": "What are the 80% and 90% rules for provisional tax?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "If your taxable income is more than R1 million, your second provisional tax estimate must be at least 80% of your actual taxable income for the year — otherwise SARS adds an underestimation penalty of 20% of the shortfall. If taxable income is R1 million or less, you must either use the SARS basic amount or estimate at least 90% of actual taxable income to avoid the penalty. We track this for our clients to avoid surprises."
      }
    },
    {
      "@type": "Question",
      "name": "When must my business register for VAT in South Africa?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Compulsory VAT registration kicks in when your taxable supplies exceed R2,300,000 in any consecutive 12-month period (effective 1 April 2026), or when you have a contractual obligation to exceed R2,300,000 in the next 12 months. Voluntary VAT registration is available from R120,000 per year in past 12-month turnover. Once registered, you submit VAT201 returns either monthly (Category C, turnover above R30 million) or bi-monthly (Categories A and B)."
      }
    },
    {
      "@type": "Question",
      "name": "How does the VAT201 return work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The VAT201 declares output VAT (charged on sales) less input VAT (paid on business expenses with valid tax invoices). The difference is paid to SARS or refunded. Bi-monthly VAT201s are due by the 25th of the month after the tax period. Refund returns over R1,000 trigger an automated SARS verification — we lodge the supporting documents the same day to release the refund."
      }
    },
    {
      "@type": "Question",
      "name": "What is a SARS verification and how is it different from an audit?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A verification is a desk review where SARS asks for supporting documents (bank statements, invoices, calculations) to verify a return. It must be resolved within 21 business days. An audit is a deeper, scoped investigation that can run for months and may extend into prior years. We handle both — uploading documents through eFiling, responding to queries, and challenging incorrect assessments via objection (NOO) and appeal (NOA)."
      }
    },
    {
      "@type": "Question",
      "name": "What penalties does SARS charge for late submissions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For outstanding income tax returns SARS charges administrative penalties of R250 to R16,000 per month outstanding, depending on your taxable income. Late VAT201 returns trigger a 10% late payment penalty plus interest. Late EMP201 returns also trigger 10% plus interest. Penalties compound monthly, so the cost of waiting grows quickly."
      }
    },
    {
      "@type": "Question",
      "name": "Can you apply for my SARS Tax Clearance / TCS PIN?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. SARS no longer issues paper Tax Clearance Certificates — they have been replaced by the Tax Compliance Status (TCS) PIN, which third parties verify on eFiling. We apply for Good Standing, Tender, Foreign Investment Allowance or Emigration TCS PINs on your behalf and resolve any compliance issues blocking the application."
      }
    },
    {
      "@type": "Question",
      "name": "Do you handle SARS objections (NOO) and appeals (NOA)?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. If you receive an additional assessment you disagree with, we lodge the Notice of Objection within 80 business days, with the supporting calculations and case law where relevant. If the objection is disallowed we lodge a Notice of Appeal and, where the amounts justify it, refer the matter to the Tax Board or Tax Court."
      }
    },
    {
      "@type": "Question",
      "name": "Do you support dormant companies?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Even a dormant company must file a nil ITR14, a CIPC annual return and (if registered) nil VAT201 and EMP201 returns to stay compliant. Our Basic package covers dormant companies at R300/month."
      }
    }
  ]
};


// Annual Financial Statements schema
export const afsServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Annual Financial Statements & CIPC Compliance — South Africa",
  "provider": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa",
    "url": "https://bookkeepingservices.co.za"
  },
  "description": "Professionally prepared annual financial statements, CIPC annual returns, beneficial ownership submissions, and workmen's compensation returns for South African businesses.",
  "areaServed": { "@type": "Country", "name": "South Africa" },
  "serviceType": ["Annual Financial Statements", "CIPC Annual Returns", "Beneficial Ownership Submissions", "Workmen's Compensation Returns"]
};

export const afsFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What are annual financial statements (AFS)?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Annual financial statements are the formal year-end accounts of a business. They include a statement of financial position (balance sheet), statement of comprehensive income (profit and loss), statement of changes in equity, statement of cash flows and supporting notes. They are prepared in accordance with IFRS for SMEs (most Pty Ltds) or full IFRS (regulated entities) and are signed off by the directors. SARS requires AFS to support the ITR14 and CIPC may request them where a Public Interest Score makes them mandatory."
      }
    },
    {
      "@type": "Question",
      "name": "Does every South African company need audited AFS?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. The Companies Act uses the Public Interest Score (PIS) to determine whether AFS must be audited, independently reviewed or only compiled. Most owner-managed Pty (Ltd) companies with a low PIS only need independently compiled AFS. Audits are required where the PIS is 350+, or 100+ if the AFS are compiled externally, or where required by the company's MOI."
      }
    },
    {
      "@type": "Question",
      "name": "What is a CIPC annual return and when is it due?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The CIPC annual return is a yearly filing that confirms your company's details (registered address, directors, turnover) and pays a small fee to CIPC. It is due within 30 business days of your company's registration anniversary. Missing the annual return triggers a final deregistration process — once deregistered, your bank accounts can be frozen and contracts in the company's name can be challenged."
      }
    },
    {
      "@type": "Question",
      "name": "Is a CIPC annual return the same as a SARS tax return?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. They are filed with different bodies for different purposes. The CIPC annual return is a corporate-registry filing that keeps your company on the register. The SARS ITR14 is an income tax return that calculates tax payable. You must file both every year, and they are not interchangeable."
      }
    },
    {
      "@type": "Question",
      "name": "What is the Beneficial Ownership (BO) filing?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Since the Companies Amendment Act came into force in 2023, all South African companies must file a Beneficial Ownership register with CIPC listing the natural persons who ultimately own or control 5% or more of the company. The BO filing must be lodged within 10 business days of the CIPC annual return and updated whenever ownership changes."
      }
    },
    {
      "@type": "Question",
      "name": "My company was deregistered — can you restore it?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. We file Form CoR 40.5 with CIPC together with the outstanding annual returns, BO registers, tax compliance evidence and supporting documents. Restoration typically takes 4–8 weeks. We also catch up any outstanding SARS submissions so that the restored company is fully compliant."
      }
    },
    {
      "@type": "Question",
      "name": "Do you also file the Workmen's Compensation Return of Earnings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The annual Return of Earnings (W.As.8) is submitted to the Compensation Fund every year before the deadline (typically end of May). It declares total earnings for the prior year and triggers the assessment for COIDA cover. We file it as part of our Medium and bespoke packages."
      }
    },
    {
      "@type": "Question",
      "name": "What is the Public Interest Score and why does it matter?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Public Interest Score (PIS) is a Companies Act formula based on number of employees, third-party liabilities, turnover and number of beneficial owners. It determines (a) whether your AFS must be audited, reviewed or only compiled, and (b) whether they must be filed with CIPC together with the annual return. We calculate the PIS as part of every AFS engagement so the correct assurance level is applied."
      }
    },
    {
      "@type": "Question",
      "name": "How long does it take to prepare AFS?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "If we run the monthly bookkeeping, AFS are usually finalised within 2–3 weeks of the financial year-end. If we are preparing AFS for a new client based on records they provide, it typically takes 3–6 weeks depending on how complete and reconciled the records are."
      }
    }
  ]
};


// Packages page FAQ
export const packagesFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How much do bookkeeping services cost in South Africa?",
      "acceptedAnswer": { "@type": "Answer", "text": "Our packages start at R300/month for the Basic package (sole proprietors and dormant companies), R2,500/month for Entry (turnover up to R2.3m — bookkeeping, payroll for 1–5 employees, EMP201, EMP501, UIF, ITR14 and CIPC), and R4,500/month for Medium (turnover up to R12m — adds VAT201, payroll for 1–10 employees and the COIDA Return of Earnings). Pricing is fixed and includes all listed SARS and CIPC submissions." }
    },
    {
      "@type": "Question",
      "name": "What is included in the monthly bookkeeping packages?",
      "acceptedAnswer": { "@type": "Answer", "text": "Each package is all-inclusive: monthly bookkeeping (on Xero, Sage, Pastel or your preferred system), bank reconciliations, payroll processing, monthly EMP201 and bi-annual EMP501 submissions, UIF declarations via uFiling, the annual ITR14 (or ITR12 for sole proprietors), provisional tax (IRP6 × 2), CIPC annual returns, and Beneficial Ownership filings. The Medium package adds VAT201 submissions and the COIDA Return of Earnings." }
    },
    {
      "@type": "Question",
      "name": "Are there any hidden fees?",
      "acceptedAnswer": { "@type": "Answer", "text": "No. The monthly fee covers everything in the package. Add-ons that fall outside scope — company registration, name changes, share transfers, tax clearance for tenders or emigration, restoration of deregistered companies, catch-up bookkeeping for prior years, audit support beyond routine SARS verifications — are quoted upfront and only proceed once approved." }
    },
    {
      "@type": "Question",
      "name": "Can I switch packages as my business grows?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes. You can move between packages at any month-end. Most clients move from Entry to Medium once they cross R2,300,000 in turnover (the compulsory VAT registration threshold effective 1 April 2026) or hire their 6th employee. We pro-rata the change so you only pay the new fee from the month it takes effect." }
    },
    {
      "@type": "Question",
      "name": "Do I need to be VAT registered to use your services?",
      "acceptedAnswer": { "@type": "Answer", "text": "No. We work with both VAT and non-VAT vendors. If you are not yet at the R2,300,000 compulsory threshold (effective 1 April 2026) but want to register voluntarily (available from R120,000 per year turnover), we handle the VAT101 registration with SARS and add VAT201 submissions to your package." }
    },
    {
      "@type": "Question",
      "name": "Is there a long-term contract?",
      "acceptedAnswer": { "@type": "Answer", "text": "No. Our packages are billed month-to-month. You can cancel with one calendar month's notice — we hand over your accounting file, payroll history and SARS access cleanly so a new bookkeeper can pick up without disruption." }
    },
    {
      "@type": "Question",
      "name": "How do I pay — debit order or invoice?",
      "acceptedAnswer": { "@type": "Answer", "text": "Most clients pay by monthly debit order, which keeps the admin simple and the price the same every month. We can also invoice you on the 1st of each month with EFT payment terms if you prefer." }
    },
    {
      "@type": "Question",
      "name": "Are management accounts included?",
      "acceptedAnswer": { "@type": "Answer", "text": "Monthly profit and loss and balance sheet reports are included in the Entry and Medium packages. Detailed management packs with budget vs actual, cash flow forecasts and KPI dashboards are available as an add-on or as part of a custom advisory engagement." }
    }
  ]
};


// City-specific Service schema. `path` is the full route path from root.
export const buildCityServiceSchema = (city: string, province: string, path: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Bookkeeping Service",
  "name": `Bookkeeping Services in ${city}`,
  "description": `SAICA registered bookkeeping, payroll, VAT, tax returns and SARS compliance services for businesses in ${city}, ${province}.`,
  "url": `https://bookkeepingservices.co.za${path.startsWith("/") ? path : `/${path}`}`,
  "provider": {
    "@type": "AccountingService",
    "name": "Bookkeeping Services South Africa",
    "url": "https://bookkeepingservices.co.za",
    "telephone": "+27-73-250-9394"
  },
  "areaServed": { "@type": "City", "name": city, "containedInPlace": { "@type": "AdministrativeArea", "name": province } },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": `Bookkeeping Services — ${city}`,
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Monthly Bookkeeping" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Payroll (EMP201 & EMP501)" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "VAT & UIF Declarations" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Annual Financial Statements" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Tax Returns (ITR14 & Provisional)" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "CIPC Annual Returns" } }
    ]
  }
});

// City-specific FAQ schema
export const buildCityFaqSchema = (city: string) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": `Do you offer bookkeeping services in ${city}?`,
      "acceptedAnswer": { "@type": "Answer", "text": `Yes. We provide full bookkeeping, payroll, VAT and SARS compliance services to businesses across ${city} and the surrounding areas. All work is performed by SAICA registered professionals — we work with Xero, Sage, Pastel and other accounting systems — and our service is fully remote so we work with clients in every suburb.` }
    },
    {
      "@type": "Question",
      "name": `How much does a bookkeeper cost in ${city}?`,
      "acceptedAnswer": { "@type": "Answer", "text": `Our packages start at R300/month for nil-turnover companies, R2,500/month for Entry Level (turnover up to R2.3m) and R4,500/month for the Medium package (turnover up to R12m, includes VAT). Pricing is the same nationally — you get the same SAICA registered service regardless of where in ${city} your business is based.` }
    },
    {
      "@type": "Question",
      "name": "Do I need to meet in person?",
      "acceptedAnswer": { "@type": "Answer", "text": "No. All onboarding, monthly reporting and SARS submissions are handled remotely via your accounting software (Xero, Sage, Pastel or similar), email and WhatsApp. We can arrange video calls whenever you'd like to talk through your numbers." }
    },
    {
      "@type": "Question",
      "name": "How quickly can you take over my books?",
      "acceptedAnswer": { "@type": "Answer", "text": "Most clients are fully onboarded within 48 hours. If your books are behind we'll quote separately for the catch-up work and have you current within 2-4 weeks." }
    },
    {
      "@type": "Question",
      "name": "Are you registered with SARS and SAICA?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes. Our practice is SAICA registered and our tax practitioners are registered with SARS. This means we can submit returns, represent you in SARS queries and sign off on financial statements." }
    }
  ]
});

// Breadcrumb schema generator
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});
